<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetRentalGeneralData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    rental: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const units = ref(['ha', 'm2']);
  const categories = ref(null);
  const ux = ref([]);
  const b_6917646200545441_parameters = computed(() => ({
    role: 'own',
  }));
  const b_6424600534600827_parameters = computed(() => ({
    type: 'property',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    units: units.value,
    categories: categories.value,
    ux: ux.value,
    b_6917646200545441_parameters: b_6917646200545441_parameters.value,
    b_6424600534600827_parameters: b_6424600534600827_parameters.value,
  }));

  const $$load_categories = async () => {
    return $fn.value
      .fetch('get_tags', { parameters: { tag_type: 'rental' } })
      .then((data) => _.map(data, (p) => p.name));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'units' },
      { name: 'categories' },
      { name: 'ux' },
      { name: 'b_6917646200545441_parameters', nodeId: '6917646200545441' },
      { name: 'b_6424600534600827_parameters', nodeId: '6424600534600827' },
    ],
    loaders: { categories: $$load_categories },
    ux: {},
    dataRefs: {
      labelAlign,
      units,
      categories,
      ux,
      b_6917646200545441_parameters,
      b_6424600534600827_parameters,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.rental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rental');
    }
  );

  const act_7315092382398562_reloadComponentData_255lt2446 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_f5nmg777t =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_255lt2446,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_f5nmg777t],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_f5nmg777t,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_f5nmg777t = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_f5nmg777t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend class="text-muted">
      {{ pgettext('Rental', 'General') }}
    </legend>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="name"
      :label="pgettext('Rental', 'Name')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="name"
        v-model="rental.name"
        :value="rental.name"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.name =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
        autofocus
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Rental', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea
        name="description"
        v-model="rental.description"
        :value="rental.description"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.description =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="company"
      :label="pgettext('Rental', 'Company')"
      :labelAlign="labelAlign"
      required
    >
      <UcCompanySelector
        :item="rental"
        path="company"
        :parameters="b_6917646200545441_parameters"
        required
      >
      </UcCompanySelector>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="site_id"
      :label="pgettext('Rental', 'Site')"
      :labelAlign="labelAlign"
    >
      <UcSiteSelector
        :item="rental"
        path="site"
        :parameters="b_6424600534600827_parameters"
        :initialOptions="[rental.site]"
      >
      </UcSiteSelector>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="categories"
      :label="pgettext('Rental', 'Categories')"
      :labelAlign="labelAlign"
    >
      <ItpSelect2
        name="categories"
        multiple
        taggable
        :options="categories"
        v-model="rental.categories"
        :value="rental.categories"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.categories =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="location_code"
      :label="pgettext('Rental', 'Location')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="location_code"
        v-model="rental.location_code"
        :value="rental.location_code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.location_code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="lot_number"
      :label="pgettext('Rental', 'Lot number')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="lot_number"
        v-model="rental.lot_number"
        :value="rental.lot_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.lot_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="house_number"
      :label="pgettext('Rental', 'Building number')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="house_number"
        v-model="rental.house_number"
        :value="rental.house_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.house_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :label="pgettext('Rental', 'Area')"
      :labelAlign="labelAlign"
      contentClass="input-group"
    >
      <ItpFormInput
        type="number"
        name="area"
        v-model="rental.area"
        :value="rental.area"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.area =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="col-8"
      >
      </ItpFormInput>
      <ItpFormSelect2
        name="unit_of_area"
        v-model="rental.unit_of_area"
        :value="rental.unit_of_area"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.unit_of_area =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="units"
        class="col-4"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :label="pgettext('Rental', 'Net floor area')"
      :labelAlign="labelAlign"
      contentClass="input-group"
    >
      <ItpFormInput
        type="number"
        name="net_floor_area"
        v-model="rental.net_floor_area"
        :value="rental.net_floor_area"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.net_floor_area =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="col-8"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :label="pgettext('Rental', 'Net floor area factor')"
      :labelAlign="labelAlign"
      contentClass="input-group"
    >
      <ItpFormInput
        type="number"
        name="net_floor_area_factor"
        v-model="rental.net_floor_area_factor"
        :value="rental.net_floor_area_factor"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (rental.net_floor_area_factor =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="col-8"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <FieldsetRentalValidityData :rental="rental" :labelAlign="labelAlign">
    </FieldsetRentalValidityData>
  </ItpUsercontrol>
</template>
